<script lang="ts" setup>
import type { Bundle, TableRow } from '~~/server/api/insurance/product'

export interface PackageListProps {
    packages: Bundle
}

export interface TableSection {
    title: string | null
    rows: TableRow[]
}

const props = defineProps<PackageListProps>()

const thead = computed<TableRow | null>((): TableRow | null => props.packages?.table_rows?.length ? props.packages.table_rows[0] ?? null : null)

// Convert thead object to array for proper iteration with numeric indexes
const theadArray = computed(() => thead.value ? Object.values(thead.value) : [])

// Helper function to check if a row is a section header
// A section header has: value_type === 'group' OR only the first cell has text (others empty)
const isGroupRow = (row: TableRow): boolean => {
    const cells = Object.values(row)
    const firstCell = cells[0]

    // Check explicit group marker
    if (firstCell?.value_type === 'group') {
        return true
    }

    // Check if only first cell has text and others are empty/no icons
    if (cells.length > 1 && firstCell?.text) {
        const otherCells = cells.slice(1)
        const allOthersEmpty = otherCells.every((cell) =>
            !cell?.text && cell?.value_type !== 'icon'
        )
        if (allOthersEmpty) {
            return true
        }
    }

    return false
}

// Split table rows into sections based on group headers
// This ensures each logical table has its own HTML table element (WCAG 1.3.1)
const tableSections = computed<TableSection[]>(() => {
    if (!props.packages?.table_rows?.length || props.packages.table_rows.length < 2) {
        return []
    }

    const allRows = props.packages.table_rows.slice(1) // Skip header row
    const sections: TableSection[] = []
    let currentSection: TableSection = { title: null, rows: [] }

    allRows.forEach((row) => {
        if (isGroupRow(row)) {
            // Save previous section if it has rows
            if (currentSection.rows.length > 0) {
                sections.push(currentSection)
            }
            // Start new section with group title
            const firstCell = Object.values(row)[0]
            currentSection = {
                title: firstCell?.text || null,
                rows: []
            }
        } else {
            // Add row to current section
            currentSection.rows.push(row)
        }
    })

    // Don't forget the last section
    if (currentSection.rows.length > 0) {
        sections.push(currentSection)
    }

    return sections
})
</script>

<template>
  <section data-cy="insurance_package_list">
    <Tracker :data="{ position: props.packages?.title || '' }">
      <BoxHeader
        :heading="props.packages?.title"
        heading-level="h2"
        :subtitle="props.packages?.subtitle"
      >
        <template #tag>
          <Tag
            background-color="gray-1"
            text-color="black-100"
            classes="text-uppercase"
            size="small"
          >
            {{ props.packages?.tag }}
          </Tag>
        </template>
      </BoxHeader>

      <div class="package-list rounded px-md-4 py-md-5">
        <div
          class="package-list-swipe rounded d-md-none ms-0 ms-md-0 mb-5 mb-md-6 px-2 pt-4 pb-2"
        >
          <img
            loading="lazy"
            decoding="async"
            class="d-block mx-auto mb-2"
            src="~/assets/icons/swipe.svg"
            alt=""
            width="28"
            height="27"
          >
          <div class="fs-xsmall fw-bold text-center">
            {{ $t('Tabuľku posúvaj posunom do strán') }}
          </div>
        </div>
        <div class="package-list-table-wrap">
          <!-- Render separate tables for each section (WCAG 1.3.1) -->
          <template
            v-for="(section, sectionIndex) in tableSections"
            :key="`section-${sectionIndex}`"
          >
            <!-- Section heading (was previously a group row inside the table) -->
            <h3
              v-if="section.title"
              class="fs-normal fs-md-biggest fw-bold ps-2 ps-md-4 pt-4 pb-1 mb-0"
            >
              {{ section.title }}
            </h3>

            <table
              v-if="thead && section.rows.length"
              class="package-list-table w-100 text-start fs-xsmall fs-md-smaller text-black"
              :class="{ 'mt-0': section.title }"
            >
              <caption class="visually-hidden">
                {{ section.title || props.packages?.title }}
              </caption>
              <thead class="package-list-table-head">
                <tr class="package-list-table-header">
                  <th
                    v-for="({ text }, index) in theadArray"
                    :key="`plthi-${sectionIndex}-${index}`"
                    scope="col"
                    :tabindex="index === 0 ? 0 : undefined"
                    :aria-label="!text ? $t('Prázdna bunka') : undefined"
                    class="pb-2 pb-md-4 pt-md-7"
                    :class="
                      index === 0
                        ? 'fs-normal fs-md-biggest ps-2 ps-md-4'
                        : 'gamma text-center'
                    "
                  >
                    {{ text }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(cells, rowIndex) in section.rows"
                  :key="`row-${sectionIndex}-${rowIndex}`"
                >
                  <template
                    v-for="(cell, cellIndex) in Object.values(cells)"
                    :key="`tc-${sectionIndex}-${rowIndex}-${cellIndex}`"
                  >
                    <!-- First cell is row header -->
                    <th
                      v-if="cellIndex === 0"
                      scope="row"
                      tabindex="0"
                      class="ps-2 py-2 py-md-3 ps-md-4 fw-normal"
                    >
                      {{ cell.text }}
                      <Tooltip
                        v-if="cell.tooltip"
                        :title="cell.tooltip"
                        position="right"
                      >
                        <svg
                          aria-hidden="true"
                          width="12"
                          height="12"
                        >
                          <use xlink:href="#info" />
                        </svg>
                      </Tooltip>
                    </th>
                    <!-- Data cells -->
                    <td
                      v-else
                      class="text-center"
                      :aria-label="!cell.text ? $t('Prázdna bunka') : undefined"
                    >
                      <template v-if="cell.value_type === 'text'">
                        {{ cell.text }}
                        <Tooltip
                          v-if="cell.tooltip"
                          :title="cell.tooltip"
                          position="right"
                        >
                          <svg
                            aria-hidden="true"
                            width="12"
                            height="12"
                          >
                            <use xlink:href="#info" />
                          </svg>
                        </Tooltip>
                      </template>
                      <template v-if="cell.value_type === 'icon'">
                        <svg
                          aria-hidden="true"
                          width="16"
                          height="16"
                          class="text-secondary"
                          :aria-label="$t('Áno')"
                        >
                          <use xlink:href="#checked" />
                        </svg>
                        <span class="visually-hidden">{{ $t('Áno') }}</span>
                      </template>
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </template>
        </div>
        <div class="d-flex justify-content-center mt-2 mt-md-4">
          <Tracker :data="{ title: props?.packages?.cta?.text }">
            <VButton
              v-if="props.packages?.cta"
              color="primary"
              :to="props.packages.cta.link"
              :label="props.packages.cta.link"
            >
              {{ props?.packages?.cta?.text }}
            </VButton>
          </Tracker>
        </div>
      </div>
      <IconsWrapper>
        <IconsSymbolChecked />
        <IconsSymbolInfo />
      </IconsWrapper>
    </Tracker>
  </section>
</template>

<style lang="scss">
@import './PackageList.vars.scss';
</style>

<style lang="scss" scoped>
@import './PackageList.scss';
</style>
